/**
 * VATConfigCard.js - Configuração de IVA
 * Componente para gerir configuração de IVA/VAT do sistema
 * Moçambique: Taxa padrão 16%
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Percent, Save, RefreshCw, Calculator, Info } from 'lucide-react';
import { vatAPI, getErrorMessage } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useLanguage } from '@/i18n';

const VAT_REGIMES = [
  { value: 'inclusive', label: 'IVA Incluído', description: 'O preço indicado já inclui IVA' },
  { value: 'exclusive', label: 'IVA Excluído', description: 'IVA será adicionado ao preço' },
  { value: 'exempt', label: 'Isento', description: 'Artigo isento de IVA' },
];

export default function VATConfigCard() {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    default_rate: 16.0,
    default_regime: 'inclusive'
  });
  
  // Test calculation
  const [testPrice, setTestPrice] = useState(1000);
  const [testResult, setTestResult] = useState(null);

  const applyPreset = (preset) => {
    setFormData({
      default_rate: preset.rate,
      default_regime: preset.regime,
    });
  };
  
  useEffect(() => {
    loadConfig();
  }, []);
  
  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await vatAPI.getConfig();
      setConfig(response.data);
      setFormData({
        default_rate: response.data.default_rate,
        default_regime: response.data.default_regime
      });
    } catch (error) {
      toast.error(getErrorMessage(error, t('vatConfigPage.loadError')));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      await vatAPI.updateConfig(formData);
      toast.success(t('vatConfigPage.saveSuccess'));
      loadConfig();
    } catch (error) {
      toast.error(getErrorMessage(error, t('vatConfigPage.saveError')));
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestCalculation = async () => {
    try {
      const response = await vatAPI.calculate(testPrice, formData.default_rate, formData.default_regime);
      setTestResult(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, t('vatConfigPage.calculateError')));
    }
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Main Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-primary" />
            {t('vatConfigPage.title')}
          </CardTitle>
          <CardDescription>
            {t('vatConfigPage.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">{t('vatConfigPage.rulesTitle')}</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>O IVA é explicitamente tratado em <strong>todos os documentos</strong></li>
                <li>Utilizador pode escolher regime por produto, documento ou usar o padrão</li>
                <li>Todos os documentos fiscais mostram: Base Tributável + IVA + Total</li>
              </ul>
            </div>
          </div>
          
          {/* Form Fields */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="vat_rate">{t('vatConfigPage.defaultRate')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="vat_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.default_rate}
                  onChange={(e) => setFormData({ ...formData, default_rate: parseFloat(e.target.value) || 0 })}
                  className="max-w-[120px]"
                  data-testid="vat-rate-input"
                />
                <span className="text-muted-foreground">%</span>
                <Badge variant="outline" className="ml-2">{t('vatConfigPage.mozRate')}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('vatConfigPage.defaultRateHint')}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="vat_regime">{t('vatConfigPage.defaultRegime')}</Label>
              <Select
                value={formData.default_regime}
                onValueChange={(value) => setFormData({ ...formData, default_regime: value })}
              >
                <SelectTrigger id="vat_regime" data-testid="vat-regime-select">
                  <SelectValue placeholder={t('vatConfigPage.selectRegime')} />
                </SelectTrigger>
                <SelectContent>
                  {VAT_REGIMES.map((regime) => (
                    <SelectItem key={regime.value} value={regime.value}>
                      <div className="flex flex-col">
                        <span>{regime.label}</span>
                        <span className="text-xs text-muted-foreground">{regime.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('vatConfigPage.defaultRegimeHint')}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('vatConfigPage.quickPresets')}</Label>
            <div className="flex flex-wrap gap-2" data-testid="vat-presets-group">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPreset({ rate: 16, regime: 'inclusive' })}
                data-testid="vat-preset-inclusive-16"
              >
                16% Incluído
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPreset({ rate: 16, regime: 'exclusive' })}
                data-testid="vat-preset-exclusive-16"
              >
                16% Excluído
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyPreset({ rate: 0, regime: 'exempt' })}
                data-testid="vat-preset-exempt"
              >
                Isento
              </Button>
            </div>
          </div>
          
          {/* Regime Explanation */}
          <div className="grid gap-4 md:grid-cols-3">
            {VAT_REGIMES.map((regime) => (
              <div 
                key={regime.value}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  formData.default_regime === regime.value 
                    ? 'border-primary bg-primary/5' 
                    : 'border-muted bg-muted/20'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={formData.default_regime === regime.value ? 'default' : 'outline'}>
                    {regime.label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{regime.description}</p>
                {regime.value === 'inclusive' && (
                  <p className="text-xs mt-2 text-green-600">
                    Ex: Preço 1.160 MT = 1.000 MT base + 160 MT IVA
                  </p>
                )}
                {regime.value === 'exclusive' && (
                  <p className="text-xs mt-2 text-blue-600">
                    Ex: Preço 1.000 MT + 160 MT IVA = 1.160 MT total
                  </p>
                )}
                {regime.value === 'exempt' && (
                  <p className="text-xs mt-2 text-gray-600">
                    Ex: Preço 1.000 MT = 1.000 MT total (sem IVA)
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-xl border bg-muted/20 p-4" data-testid="vat-default-impact-preview">
            <p className="text-sm font-medium">{t('vatConfigPage.impactPreview')}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('vatConfigPage.impactDescription', { value: formatCurrency(1000) })}
              {' '}
              <strong>
                {formData.default_regime === 'inclusive'
                  ? `${formatCurrency(1000 + (1000 * formData.default_rate / 100))} com IVA incluído`
                  : formData.default_regime === 'exclusive'
                    ? `${formatCurrency(1000)} + IVA ${formatCurrency(1000 * formData.default_rate / 100)}`
                    : `${formatCurrency(1000)} (isento)`}
              </strong>
            </p>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={loadConfig} disabled={saving}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('actions.refresh')}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-vat-config-btn">
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('vatConfigPage.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('vatConfigPage.saveButton')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Test Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5 text-primary" />
            {t('vatConfigPage.calculatorTitle')}
          </CardTitle>
          <CardDescription>
            {t('vatConfigPage.calculatorDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="test_price">{t('vatConfigPage.testPrice')}</Label>
              <Input
                id="test_price"
                type="number"
                min="0"
                step="0.01"
                value={testPrice}
                onChange={(e) => setTestPrice(parseFloat(e.target.value) || 0)}
                className="max-w-[200px]"
                data-testid="test-price-input"
              />
            </div>
            <Button onClick={handleTestCalculation} variant="outline" data-testid="calculate-vat-btn">
              <Calculator className="mr-2 h-4 w-4" />
              {t('vatConfigPage.calculateButton')}
            </Button>
          </div>
          
          {testResult && (
            <div className="grid gap-4 md:grid-cols-4 p-4 bg-muted/30 rounded-lg" data-testid="vat-calculation-result">
              <div>
                <p className="text-sm text-muted-foreground">Regime</p>
                <p className="font-medium">
                  {VAT_REGIMES.find(r => r.value === testResult.regime)?.label || testResult.regime}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Base Tributável</p>
                <p className="font-medium text-lg">{formatCurrency(testResult.base_price)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IVA ({testResult.vat_rate}%)</p>
                <p className="font-medium text-lg text-primary">{formatCurrency(testResult.vat_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="font-bold text-xl text-green-600">{formatCurrency(testResult.total_price)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Current Config Summary */}
      {config && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('vatConfigPage.currentConfig')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Taxa Padrão</p>
                <p className="text-2xl font-bold text-primary">{config.default_rate}%</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Regime Padrão</p>
                <p className="text-lg font-medium">
                  {VAT_REGIMES.find(r => r.value === config.default_regime)?.label || config.default_regime}
                </p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Última Actualização</p>
                <p className="text-sm">
                  {config.updated_at ? new Date(config.updated_at).toLocaleString('pt-PT') : 'N/A'}
                </p>
                {config.updated_by_name && (
                  <p className="text-xs text-muted-foreground">por {config.updated_by_name}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
